import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryOptions } from '@tanstack/react-query';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { T, sans, card, linkBtn } from './VendorDetails';

/* ── Types ─────────────────────────────────────────────────────────── */
type AnalysisType = 'cyber_privacy' | 'legal_risk';
type AnalysisStatus = 'pending' | 'completed' | 'failed';
type OverallRating = 'strong' | 'adequate' | 'needs_attention' | 'high_risk';
type FindingRating = 'adequate' | 'weak' | 'missing' | 'not_applicable';

interface Finding { key: string; label: string; rating: FindingRating; confidence: number; rationale: string; quote: string | null; }
interface Analysis {
  status: AnalysisStatus; overall_rating: OverallRating | null; summary: string | null;
  findings: Finding[]; model: string | null; truncated: boolean; error: string | null; completed_at: string | null;
}
interface Contract {
  id: string; file_name: string; content_type: string | null; size_bytes: number | null; security_flags: string | null;
  contract_name: string | null; contract_type: string | null; counterparty_name: string | null;
  effective_date: string | null; expiry_date: string | null; contract_value: string | null; notes: string | null;
  source: string; uploaded_by_name: string | null; created_at: string; updated_at: string;
  analyses: Partial<Record<AnalysisType, Analysis>>;
}
interface Connector { key: string; name: string; domain: string; status: 'coming_soon'; }

interface Props { vendorId: string; vendorName?: string; isDark: boolean; }

/* ── Static config ────────────────────────────────────────────────── */
const LENSES: { value: AnalysisType; label: string; blurb: string }[] = [
  { value: 'cyber_privacy', label: 'Cyber & Privacy', blurb: 'Data protection, breach notification, security controls, subprocessors, audits, residency, retention, insurance.' },
  { value: 'legal_risk', label: 'Legal Risk', blurb: 'Liability caps, indemnification, termination, renewal, governing law, SLAs, IP, assignment, exclusivity.' },
];
const CONTRACT_TYPES = ['MSA', 'DPA', 'SOW', 'SLA', 'NDA', 'Order Form', 'Amendment', 'Other'];
const OVERALL_META: Record<OverallRating, { label: string; fg: string; bg: string; darkFg: string }> = {
  strong: { label: 'Strong', fg: '#166534', bg: '#DCFCE7', darkFg: '#4ADE80' },
  adequate: { label: 'Adequate', fg: '#0E7490', bg: '#CFFAFE', darkFg: '#22D3EE' },
  needs_attention: { label: 'Needs attention', fg: '#92400E', bg: '#FEF3C7', darkFg: '#FBBF24' },
  high_risk: { label: 'High risk', fg: '#991B1B', bg: '#FEE2E2', darkFg: '#F87171' },
};
const FINDING_META: Record<FindingRating, { label: string; fg: string; bg: string; darkFg: string }> = {
  adequate: { label: 'Adequate', fg: '#166534', bg: '#DCFCE7', darkFg: '#4ADE80' },
  weak: { label: 'Weak', fg: '#92400E', bg: '#FEF3C7', darkFg: '#FBBF24' },
  missing: { label: 'Missing', fg: '#991B1B', bg: '#FEE2E2', darkFg: '#F87171' },
  not_applicable: { label: 'N/A', fg: '#475569', bg: '#F1F5F9', darkFg: '#94A3B8' },
};

const apiError = (error: any, fallback: string) => {
  const d = error?.response?.data?.detail;
  if (Array.isArray(d)) return d.map((x: any) => x?.msg).filter(Boolean).join('; ') || fallback;
  if (typeof d === 'string') return d;
  if (d && typeof d === 'object' && typeof d.message === 'string') return d.message;
  return fallback;
};
const fmtDate = (iso: string | null) => iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null;
const fmtSize = (bytes: number | null) => {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const Icon = ({ d, size = 14, style }: { d: string; size?: number; style?: React.CSSProperties }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, ...style }}><path d={d} /></svg>
);
const I = {
  plus: 'M12 5v14M5 12h14', close: 'M6 6l12 12M18 6 6 18', check: 'M5 12l5 5L20 7',
  download: 'M12 3v12M7 10l5 5 5-5M5 21h14', upload: 'M12 21V9M7 14l5-5 5 5M5 3h14',
  file: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z M14 2v6h6',
  trash: 'M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6', edit: 'M12 20h9M16.5 3.5a2.1 2.1 0 1 1 3 3L7 19l-4 1 1-4Z',
  link: 'M10 13a5 5 0 0 0 7.5.5l1-1a5 5 0 0 0-7-7l-1 1M14 11a5 5 0 0 0-7.5-.5l-1 1a5 5 0 0 0 7 7l1-1',
  sparkle: 'M12 3v4M12 17v4M3 12h4M17 12h4M5.6 5.6l2.8 2.8M15.6 15.6l2.8 2.8M18.4 5.6l-2.8 2.8M8.4 15.6l-2.8 2.8',
  chevronDown: 'M6 9l6 6 6-6',
};

/* ── Query ─────────────────────────────────────────────────────────── */
const contractsQueryOptions = (vendorId: string) => queryOptions({
  queryKey: ['vendors', 'detail', vendorId, 'contracts'],
  queryFn: async () => ((await api.get(`/v1/vendors/${vendorId}/contracts`)).data?.contracts || []) as Contract[],
});
const connectorsQueryOptions = () => queryOptions({
  queryKey: ['contract-connectors'],
  queryFn: async () => ((await api.get('/v1/contract-connectors')).data?.connectors || []) as Connector[],
  staleTime: 10 * 60_000,
});

export default function VendorContracts({ vendorId, vendorName, isDark }: Props) {
  const { user } = useAuth();
  const canEdit = !!user && user.role !== 'viewer';
  const canDelete = !!user && (user.role === 'admin' || user.role === 'super_admin');

  const contractsQuery = useQuery(contractsQueryOptions(vendorId));
  const connectorsQuery = useQuery(connectorsQueryOptions());
  const contracts = contractsQuery.data ?? [];

  const [showConnectors, setShowConnectors] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState<Contract | null>(null);
  const [opened, setOpened] = useState<Contract | null>(null);
  const [deleting, setDeleting] = useState<Contract | null>(null);
  const [deleteError, setDeleteError] = useState('');
  const [toast, setToast] = useState('');

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(''), 2600);
    return () => clearTimeout(t);
  }, [toast]);

  const c = {
    ink: isDark ? '#e2e8f0' : T.ink, muted: isDark ? '#94a3b8' : T.slate, faint: isDark ? '#64748b' : T.muted,
    line: isDark ? '#1f1f1f' : T.bdr, soft: isDark ? '#141414' : T.surface2, surface: isDark ? '#0d0d0d' : T.surface,
    accent: isDark ? '#60A5FA' : T.blue, accentBg: isDark ? 'rgba(59,130,246,.14)' : T.blueBg, red: isDark ? '#F87171' : T.red,
  };
  const btn = (primary = false): React.CSSProperties => ({
    display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 13px', borderRadius: 7, fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontFamily: sans,
    border: `1px solid ${primary ? (isDark ? '#3B82F6' : T.ink) : c.line}`, background: primary ? (isDark ? '#3B82F6' : T.ink) : c.surface, color: primary ? '#fff' : c.ink,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/v1/vendors/${vendorId}/contracts/${id}`),
    meta: { invalidates: [['vendors']] },
  });
  const confirmDelete = async () => {
    if (!deleting) return;
    setDeleteError('');
    try {
      await deleteMutation.mutateAsync(deleting.id);
      setToast(`${deleting.contract_name || deleting.file_name} deleted`);
      if (opened?.id === deleting.id) setOpened(null);
      setDeleting(null);
    } catch (e) {
      setDeleteError(apiError(e, 'Could not delete the contract. Try again.'));
    }
  };

  const openedLive = opened ? contracts.find(x => x.id === opened.id) || opened : null;

  if (contractsQuery.isPending) {
    return <div style={{ ...card(isDark), textAlign: 'center', padding: '40px 0', fontSize: 12.5, color: c.muted }}>Loading contracts…</div>;
  }
  if (contractsQuery.isError) {
    return (
      <div style={{ ...card(isDark), textAlign: 'center', padding: '40px 0', fontSize: 12.5, color: c.red }}>
        {apiError(contractsQuery.error, 'Could not load contracts.')} <button style={linkBtn(isDark)} onClick={() => contractsQuery.refetch()}>Retry</button>
      </div>
    );
  }

  return (
    <div style={{ ...card(isDark), padding: 20, fontFamily: sans }}>
      <style>{`
        @keyframes vctFade { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }
        @keyframes vctDrawerIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
        .vct-row:hover { border-color: ${isDark ? '#334155' : '#CBD5E1'} !important; }
        .vct-conn:hover { border-color: ${isDark ? '#334155' : '#CBD5E1'} !important; }
      `}</style>

      {/* header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: c.ink }}>Contracts</div>
          <div style={{ fontSize: 12, color: c.muted, marginTop: 2 }}>
            Your own agreements with {vendorName || 'this vendor'} -- analyzed for cyber/privacy and legal risk.
          </div>
        </div>
        {canEdit && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={btn()} onClick={() => setShowConnectors(s => !s)}><Icon d={I.link} size={13} />Pull from a system</button>
            <button style={btn(true)} onClick={() => setUploading(true)}><Icon d={I.upload} size={13} />Upload contract</button>
          </div>
        )}
      </div>

      {/* connectors */}
      {showConnectors && (
        <div style={{ border: `1px solid ${c.line}`, borderRadius: 10, padding: '14px 16px', marginBottom: 16 }}>
          <div style={{ fontSize: 12.5, color: c.muted, marginBottom: 10, lineHeight: 1.5 }}>
            Connect a contract management system to pull contracts automatically. None of these are connected yet --
            upload a file in the meantime, or let us know which system you use.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 8 }}>
            {(connectorsQuery.data ?? []).map(conn => (
              <div key={conn.key} className="vct-conn" style={{
                display: 'flex', alignItems: 'center', gap: 9, padding: '9px 11px', borderRadius: 8, border: `1px solid ${c.line}`,
                background: c.surface, transition: 'border-color .12s',
              }}>
                <img src={`https://www.google.com/s2/favicons?domain=${conn.domain}&sz=64`} alt="" width={18} height={18} style={{ borderRadius: 4, flexShrink: 0 }}
                  onError={e => { (e.target as HTMLImageElement).style.visibility = 'hidden'; }} />
                <span style={{ flex: 1, fontSize: 12.5, fontWeight: 600, color: c.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{conn.name}</span>
                <span style={{ fontSize: 10.5, fontWeight: 700, color: c.faint, background: c.soft, padding: '2px 7px', borderRadius: 99, flexShrink: 0 }}>Coming soon</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* list */}
      {contracts.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '36px 0 20px' }}>
          <div style={{ width: 44, height: 44, borderRadius: '50%', margin: '0 auto 10px', background: c.soft, color: c.faint, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon d={I.file} size={20} /></div>
          <div style={{ fontSize: 14, fontWeight: 700, color: c.ink }}>No contracts yet</div>
          <div style={{ fontSize: 12.5, color: c.muted, marginTop: 4 }}>Upload the agreement you have with {vendorName || 'this vendor'} to analyze it.</div>
          {canEdit && <button style={{ ...btn(true), marginTop: 14 }} onClick={() => setUploading(true)}><Icon d={I.upload} size={13} />Upload contract</button>}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {contracts.map(contract => (
            <div key={contract.id} className="vct-row" onClick={() => setOpened(contract)} style={{
              display: 'flex', alignItems: 'center', gap: 14, padding: '13px 15px', borderRadius: 10, border: `1px solid ${c.line}`,
              background: c.surface, cursor: 'pointer', transition: 'border-color .12s', flexWrap: 'wrap',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 220, flex: '1 1 220px' }}>
                <span style={{ width: 34, height: 34, borderRadius: 8, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: c.accent, background: c.accentBg }}><Icon d={I.file} size={16} /></span>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 700, color: c.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{contract.contract_name || contract.file_name}</div>
                  <div style={{ fontSize: 11.5, color: c.muted, marginTop: 1 }}>
                    {contract.contract_type ? `${contract.contract_type} · ` : ''}{contract.counterparty_name || ''}
                    {fmtSize(contract.size_bytes) ? ` · ${fmtSize(contract.size_bytes)}` : ''}
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {LENSES.map(lens => <LensPill key={lens.value} lens={lens.label} analysis={contract.analyses[lens.value]} isDark={isDark} />)}
              </div>

              <div style={{ fontSize: 11.5, color: c.faint, minWidth: 130 }}>
                {contract.expiry_date ? <>Expires {fmtDate(contract.expiry_date)}</> : contract.effective_date ? <>Effective {fmtDate(contract.effective_date)}</> : 'No dates on file'}
              </div>

              <div style={{ display: 'flex', gap: 4, marginLeft: 'auto' }} onClick={e => e.stopPropagation()}>
                <a href={`${api.defaults.baseURL}/v1/vendors/${vendorId}/contracts/${contract.id}/download`} target="_blank" rel="noreferrer"
                  title="Download" style={{ ...iconBtnStyle(c), textDecoration: 'none' }}><Icon d={I.download} size={14} /></a>
                {canEdit && <button title="Edit" onClick={() => setEditing(contract)} style={iconBtnStyle(c)}><Icon d={I.edit} size={14} /></button>}
                {canDelete && <button title="Delete" onClick={() => { setDeleting(contract); setDeleteError(''); }} style={iconBtnStyle(c)}><Icon d={I.trash} size={14} /></button>}
              </div>
            </div>
          ))}
        </div>
      )}

      {uploading && (
        <ContractForm vendorId={vendorId} vendorName={vendorName} isDark={isDark} c={c}
          onClose={() => setUploading(false)}
          onDone={() => { setUploading(false); setToast('Contract uploaded'); }} />
      )}
      {editing && (
        <ContractForm vendorId={vendorId} vendorName={vendorName} isDark={isDark} c={c} target={editing}
          onClose={() => setEditing(null)}
          onDone={() => { setEditing(null); setToast('Contract updated'); }} />
      )}
      {openedLive && (
        <ContractDrawer vendorId={vendorId} contract={openedLive} isDark={isDark} c={c} canEdit={canEdit}
          onClose={() => setOpened(null)} />
      )}

      {deleting && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300, padding: 16 }}
          onMouseDown={e => { if (e.target === e.currentTarget && !deleteMutation.isPending) setDeleting(null); }}>
          <div role="dialog" aria-modal="true" aria-label="Delete contract" style={{ width: '100%', maxWidth: 420, background: c.surface, border: `1px solid ${c.line}`, borderRadius: 10, padding: 20, fontFamily: sans }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: c.ink, marginBottom: 6 }}>Delete contract</div>
            <div style={{ fontSize: 13, color: c.muted, lineHeight: 1.5 }}>
              Delete <strong style={{ color: c.ink }}>{deleting.contract_name || deleting.file_name}</strong> and its analyses? This can't be undone.
            </div>
            {deleteError && <div style={{ fontSize: 12, color: c.red, marginTop: 10 }} role="alert">{deleteError}</div>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button style={btn()} onClick={() => setDeleting(null)} disabled={deleteMutation.isPending}>Cancel</button>
              <button style={{ ...btn(true), background: T.red, borderColor: T.red, opacity: deleteMutation.isPending ? 0.7 : 1 }} onClick={confirmDelete} disabled={deleteMutation.isPending}>
                {deleteMutation.isPending ? 'Deleting…' : 'Delete contract'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div style={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 1400, padding: '9px 16px', borderRadius: 8,
          background: isDark ? '#1e293b' : T.ink, color: '#fff', fontSize: 12.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8, animation: 'vctFade .2s ease-out', fontFamily: sans,
        }}><Icon d={I.check} size={13} />{toast}</div>
      )}
    </div>
  );
}

const iconBtnStyle = (c: any): React.CSSProperties => ({
  display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: 6,
  border: 'none', background: 'transparent', color: c.faint, cursor: 'pointer',
});

/* ── Lens status pill (on the row) ────────────────────────────────── */
function LensPill({ lens, analysis, isDark }: { lens: string; analysis: Analysis | undefined; isDark: boolean }) {
  if (!analysis || (!analysis.status)) {
    return <span style={pillStyle(isDark, '#94A3B8', 'rgba(148,163,184,.14)', '#64748B')}>{lens}: not analyzed</span>;
  }
  if (analysis.status === 'pending') {
    return <span style={pillStyle(isDark, '#92400E', '#FEF3C7', '#FBBF24')}>{lens}: analyzing…</span>;
  }
  if (analysis.status === 'failed') {
    return <span style={pillStyle(isDark, '#991B1B', '#FEE2E2', '#F87171')}>{lens}: failed</span>;
  }
  const meta = OVERALL_META[analysis.overall_rating || 'needs_attention'];
  return <span style={pillStyle(isDark, meta.fg, meta.bg, meta.darkFg)}>{lens}: {meta.label}</span>;
}
const pillStyle = (isDark: boolean, fg: string, bg: string, darkFg: string): React.CSSProperties => ({
  display: 'inline-flex', alignItems: 'center', padding: '4px 10px', borderRadius: 99, fontSize: 11, fontWeight: 600, fontFamily: sans,
  color: isDark ? darkFg : fg, background: isDark ? `${fg}26` : bg, whiteSpace: 'nowrap',
});

/* ── Upload / edit form ───────────────────────────────────────────── */
function ContractForm({ vendorId, vendorName, isDark, c, target, onClose, onDone }: {
  vendorId: string; vendorName?: string; isDark: boolean; c: any; target?: Contract; onClose: () => void; onDone: () => void;
}) {
  const isNew = !target;
  const [file, setFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [f, setF] = useState({
    contract_name: target?.contract_name || '', contract_type: target?.contract_type || '',
    counterparty_name: target?.counterparty_name || vendorName || '', effective_date: target?.effective_date || '',
    expiry_date: target?.expiry_date || '', contract_value: target?.contract_value || '', notes: target?.notes || '',
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !saving) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, saving]);

  const set = (p: Partial<typeof f>) => { setF(prev => ({ ...prev, ...p })); setError(''); };

  const submit = async () => {
    if (isNew && !file) { setError('Choose a file to upload.'); return; }
    if (f.expiry_date && f.effective_date && f.expiry_date < f.effective_date) { setError("The expiry date can't be before the effective date."); return; }
    setSaving(true);
    setError('');
    try {
      if (isNew) {
        const form = new FormData();
        form.append('file', file as File);
        Object.entries(f).forEach(([k, v]) => { if (v) form.append(k, v); });
        await api.post(`/v1/vendors/${vendorId}/contracts`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      } else {
        await api.patch(`/v1/vendors/${vendorId}/contracts/${target!.id}`, f);
      }
      onDone();
    } catch (e) {
      setError(apiError(e, 'Could not save the contract. Try again.'));
    } finally {
      setSaving(false);
    }
  };

  const input: React.CSSProperties = {
    width: '100%', boxSizing: 'border-box', padding: '8px 10px', fontSize: 13, fontFamily: sans, color: c.ink,
    background: c.surface, border: `1px solid ${c.line}`, borderRadius: 7, outline: 'none',
  };
  const label: React.CSSProperties = { display: 'block', fontSize: 11.5, fontWeight: 600, color: c.muted, marginBottom: 5 };

  return (
    <div ref={panelRef} role="dialog" aria-modal="true" aria-label={isNew ? 'Upload contract' : 'Edit contract'} style={{
      position: 'fixed', top: 64, right: 0, bottom: 0, width: 'min(460px, 100vw)', zIndex: 1300, background: c.surface,
      borderLeft: `1px solid ${c.line}`, display: 'flex', flexDirection: 'column', fontFamily: sans, animation: 'vctDrawerIn .2s ease-out',
    }}>
      <div style={{ padding: '16px 20px', borderBottom: `1px solid ${c.line}`, display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <div style={{ flex: 1 }}>
          {vendorName && <div style={{ fontSize: 11, color: c.faint, fontWeight: 600 }}>{vendorName}</div>}
          <div style={{ fontSize: 15.5, fontWeight: 700, color: c.ink, marginTop: 1 }}>{isNew ? 'Upload contract' : 'Edit contract'}</div>
        </div>
        <button onClick={onClose} aria-label="Close" style={{ border: 'none', background: 'none', color: c.muted, cursor: 'pointer', padding: 4 }}><Icon d={I.close} size={16} /></button>
      </div>

      <form noValidate onSubmit={e => { e.preventDefault(); if (!saving) submit(); }} style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <div style={{ flex: 1, overflowY: 'auto', padding: 20, display: 'grid', gap: 16, alignContent: 'start' }}>
          {isNew && (
            <div
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={e => { e.preventDefault(); setDragOver(false); const f0 = e.dataTransfer.files?.[0]; if (f0) setFile(f0); }}
              onClick={() => fileInputRef.current?.click()}
              style={{
                border: `1.5px dashed ${dragOver ? c.accent : c.line}`, borderRadius: 10, padding: '22px 16px', textAlign: 'center', cursor: 'pointer',
                background: dragOver ? c.accentBg : 'transparent',
              }}
            >
              <input ref={fileInputRef} type="file" accept=".pdf,.doc,.docx,.txt" style={{ display: 'none' }}
                onChange={e => { const f0 = e.target.files?.[0]; if (f0) setFile(f0); }} />
              <Icon d={I.upload} size={20} style={{ color: c.faint, marginBottom: 6 }} />
              <div style={{ fontSize: 12.5, color: c.ink, fontWeight: 600 }}>{file ? file.name : 'Drop a file, or click to browse'}</div>
              <div style={{ fontSize: 11, color: c.faint, marginTop: 3 }}>PDF, Word, or text -- up to 15MB</div>
            </div>
          )}
          <div>
            <label style={label} htmlFor="ctr-name">Contract name</label>
            <input id="ctr-name" style={input} value={f.contract_name} maxLength={200} placeholder="Master Services Agreement" onChange={e => set({ contract_name: e.target.value })} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={label} htmlFor="ctr-type">Type</label>
              <select id="ctr-type" style={input} value={f.contract_type} onChange={e => set({ contract_type: e.target.value })}>
                <option value="">Select type</option>
                {CONTRACT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label style={label} htmlFor="ctr-cp">Counterparty</label>
              <input id="ctr-cp" style={input} value={f.counterparty_name} maxLength={200} onChange={e => set({ counterparty_name: e.target.value })} />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={label} htmlFor="ctr-eff">Effective date</label>
              <input id="ctr-eff" type="date" style={input} value={f.effective_date} onChange={e => set({ effective_date: e.target.value })} />
            </div>
            <div>
              <label style={label} htmlFor="ctr-exp">Expiry date</label>
              <input id="ctr-exp" type="date" style={input} value={f.expiry_date} onChange={e => set({ expiry_date: e.target.value })} />
            </div>
          </div>
          <div>
            <label style={label} htmlFor="ctr-val">Contract value</label>
            <input id="ctr-val" style={input} value={f.contract_value} maxLength={100} placeholder="$50,000/year" onChange={e => set({ contract_value: e.target.value })} />
          </div>
          <div>
            <label style={label} htmlFor="ctr-notes">Notes</label>
            <textarea id="ctr-notes" rows={3} maxLength={2000} style={{ ...input, resize: 'vertical' }} value={f.notes} onChange={e => set({ notes: e.target.value })} />
          </div>
        </div>
        <div style={{ padding: '12px 20px', borderTop: `1px solid ${c.line}`, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ flex: 1, fontSize: 12, color: c.red }} role="alert">{error}</span>
          <button type="button" onClick={onClose} disabled={saving} style={{ padding: '7px 14px', borderRadius: 7, fontSize: 12.5, fontWeight: 600, border: `1px solid ${c.line}`, background: c.surface, color: c.ink, cursor: 'pointer', fontFamily: sans }}>Cancel</button>
          <button type="submit" disabled={saving} style={{ padding: '7px 14px', borderRadius: 7, fontSize: 12.5, fontWeight: 600, border: 'none', background: isDark ? '#3B82F6' : T.ink, color: '#fff', cursor: 'pointer', fontFamily: sans, opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Saving…' : isNew ? 'Upload' : 'Save changes'}
          </button>
        </div>
      </form>
    </div>
  );
}

/* ── Detail drawer: both lenses, run/re-run, criteria breakdown ──── */
function ContractDrawer({ vendorId, contract, isDark, c, canEdit, onClose }: {
  vendorId: string; contract: Contract; isDark: boolean; c: any; canEdit: boolean; onClose: () => void;
}) {
  const qc = useQueryClient();
  const [activeLens, setActiveLens] = useState<AnalysisType>('cyber_privacy');
  const [runError, setRunError] = useState('');
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const analyzeMutation = useMutation({
    mutationFn: (analysis_type: AnalysisType) => api.post(`/v1/vendors/${vendorId}/contracts/${contract.id}/analyze`, { analysis_type }),
    meta: { invalidates: [['vendors']] },
  });

  const runAnalysis = async (type: AnalysisType) => {
    setRunError('');
    try {
      await analyzeMutation.mutateAsync(type);
    } catch (e) {
      setRunError(apiError(e, 'The analysis could not be completed. Try again.'));
    } finally {
      // The route persists a 'failed' row even when the call errors (bad/missing AI key, unreadable
      // file, unparseable response), so the row's status pill must refresh on failure too -- not only
      // on success, which is all the mutation cache's own meta.invalidates covers.
      qc.invalidateQueries({ queryKey: ['vendors', 'detail', vendorId, 'contracts'] });
    }
  };

  const analysis = contract.analyses[activeLens];
  const lensMeta = LENSES.find(l => l.value === activeLens)!;
  const isRunning = analyzeMutation.isPending && analyzeMutation.variables === activeLens;

  return (
    <div ref={panelRef} role="dialog" aria-modal="true" aria-label="Contract details" style={{
      position: 'fixed', top: 64, right: 0, bottom: 0, width: 'min(560px, 100vw)', zIndex: 1300, background: c.surface,
      borderLeft: `1px solid ${c.line}`, display: 'flex', flexDirection: 'column', fontFamily: sans, animation: 'vctDrawerIn .2s ease-out',
    }}>
      <div style={{ padding: '16px 20px', borderBottom: `1px solid ${c.line}` }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15.5, fontWeight: 700, color: c.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{contract.contract_name || contract.file_name}</div>
            <div style={{ fontSize: 12, color: c.muted, marginTop: 2 }}>
              {contract.contract_type ? `${contract.contract_type} · ` : ''}{contract.counterparty_name}
              {contract.effective_date ? ` · Effective ${fmtDate(contract.effective_date)}` : ''}
              {contract.expiry_date ? ` · Expires ${fmtDate(contract.expiry_date)}` : ''}
            </div>
          </div>
          <button onClick={onClose} aria-label="Close" style={{ border: 'none', background: 'none', color: c.muted, cursor: 'pointer', padding: 4 }}><Icon d={I.close} size={16} /></button>
        </div>
        {contract.notes && <div style={{ fontSize: 12, color: c.muted, marginTop: 8, lineHeight: 1.45 }}>{contract.notes}</div>}

        {/* lens tabs */}
        <div style={{ display: 'flex', gap: 6, marginTop: 14 }}>
          {LENSES.map(lens => (
            <button key={lens.value} onClick={() => { setRunError(''); setActiveLens(lens.value); }} style={{
              flex: 1, padding: '8px 10px', borderRadius: 7, fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontFamily: sans,
              border: `1px solid ${activeLens === lens.value ? (isDark ? '#3B82F6' : T.ink) : c.line}`,
              background: activeLens === lens.value ? (isDark ? '#3B82F6' : T.ink) : c.surface,
              color: activeLens === lens.value ? '#fff' : c.ink,
            }}>{lens.label}</button>
          ))}
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
        <div style={{ fontSize: 12, color: c.muted, lineHeight: 1.5, marginBottom: 14 }}>{lensMeta.blurb}</div>

        {!analysis || analysis.status === 'failed' ? (
          <div style={{ textAlign: 'center', padding: '28px 14px', border: `1px dashed ${c.line}`, borderRadius: 10 }}>
            {analysis?.status === 'failed' && (
              <div style={{ fontSize: 12.5, color: c.red, marginBottom: 10 }}>{analysis.error || 'The last analysis failed.'}</div>
            )}
            <div style={{ fontSize: 12.5, color: c.muted, marginBottom: 12 }}>No {lensMeta.label.toLowerCase()} analysis has been run on this contract yet.</div>
            {canEdit && (
              <button onClick={() => runAnalysis(activeLens)} disabled={isRunning} style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 7, fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontFamily: sans,
                border: 'none', background: isDark ? '#3B82F6' : T.ink, color: '#fff', opacity: isRunning ? 0.7 : 1,
              }}><Icon d={I.sparkle} size={13} />{isRunning ? 'Analyzing…' : `Run ${lensMeta.label} analysis`}</button>
            )}
          </div>
        ) : analysis.status === 'pending' || isRunning ? (
          <div style={{ textAlign: 'center', padding: '28px 14px', color: c.muted, fontSize: 12.5 }}>Analyzing the contract… this can take up to a minute.</div>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <span style={pillStyle(isDark, OVERALL_META[analysis.overall_rating!].fg, OVERALL_META[analysis.overall_rating!].bg, OVERALL_META[analysis.overall_rating!].darkFg)}>
                {OVERALL_META[analysis.overall_rating!].label}
              </span>
              {canEdit && (
                <button onClick={() => runAnalysis(activeLens)} disabled={isRunning} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 10px', borderRadius: 6, fontSize: 11.5, fontWeight: 600, cursor: 'pointer', fontFamily: sans,
                  border: `1px solid ${c.line}`, background: c.surface, color: c.ink, opacity: isRunning ? 0.6 : 1,
                }}><Icon d={I.sparkle} size={12} />Re-run</button>
              )}
            </div>
            <div style={{ fontSize: 13, color: c.ink, lineHeight: 1.55, marginBottom: 6 }}>{analysis.summary}</div>
            {analysis.truncated && <div style={{ fontSize: 11.5, color: c.faint, marginBottom: 10 }}>This is a long document -- the review covers the first portion of the text.</div>}
            {analysis.model && <div style={{ fontSize: 11, color: c.faint, marginBottom: 14 }}>Reviewed by {analysis.model}{analysis.completed_at ? ` · ${fmtDate(analysis.completed_at)}` : ''}</div>}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {analysis.findings.map(f => {
                const meta = FINDING_META[f.rating];
                return (
                  <div key={f.key} style={{ border: `1px solid ${c.line}`, borderRadius: 8, padding: '10px 12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                      <span style={{ fontSize: 12.5, fontWeight: 600, color: c.ink }}>{f.label}</span>
                      <span style={pillStyle(isDark, meta.fg, meta.bg, meta.darkFg)}>{meta.label}</span>
                    </div>
                    <div style={{ fontSize: 12, color: c.muted, marginTop: 5, lineHeight: 1.45 }}>{f.rationale}</div>
                    {f.quote && <div style={{ fontSize: 11.5, color: c.faint, marginTop: 5, fontStyle: 'italic', borderLeft: `2px solid ${c.line}`, paddingLeft: 8 }}>"{f.quote}"</div>}
                  </div>
                );
              })}
            </div>
          </>
        )}
        {runError && <div style={{ fontSize: 12, color: c.red, marginTop: 12 }} role="alert">{runError}</div>}
      </div>
    </div>
  );
}
